import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { OAuthProvider, AuthMethod as SharedAuthMethod, User } from "@upsquad/shared";
import type { PersistConfig } from "redux-persist";
import { clearAccessToken, clearRefreshToken } from "@/src/common/services/http.service";
import { webStorage } from "@/src/common/services/storage.service";

type LastUsedAuthMethod = typeof SharedAuthMethod.EMAIL | OAuthProvider;

interface AuthState {
	user: User | null;
	isAuthenticated: boolean;
	lastUsedAuthMethod: LastUsedAuthMethod | null;
	refreshToken: string | null;
	impersonatorId: string | null;
}

const initialState: AuthState = {
	user: null,
	isAuthenticated: false,
	lastUsedAuthMethod: null,
	refreshToken: null,
	impersonatorId: null,
};

export const authSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		setUser: (state, { payload }: PayloadAction<User>) => {
			state.user = payload;
			state.isAuthenticated = true;
		},
		setRefreshToken: (state, { payload }: PayloadAction<string>) => {
			state.refreshToken = payload;
		},
		setLastUsedAuthMethod: (state, { payload }: PayloadAction<LastUsedAuthMethod>) => {
			state.lastUsedAuthMethod = payload;
		},
		setImpersonatorId: (state, { payload }: PayloadAction<string | null>) => {
			state.impersonatorId = payload;
		},
		logOut: (state) => {
			clearAccessToken();
			clearRefreshToken();
			return { ...initialState, lastUsedAuthMethod: state.lastUsedAuthMethod };
		},
	},
});

export const { setUser, setRefreshToken, setLastUsedAuthMethod, setImpersonatorId, logOut } = authSlice.actions;

export const authPersistConfig: PersistConfig<AuthState> = {
	key: "auth",
	storage: webStorage,
	whitelist: ["user", "isAuthenticated", "lastUsedAuthMethod", "refreshToken", "impersonatorId"],
};
